import { useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { request } from "app/network";
import { formatLocalDateTime } from "utils/dateFormatter";
import fireAlert from "utils/sweetAlert";
import { useMaterialUIController } from "context";
import { GenericListScreen } from "components/GenericListScreen/GenericListScreen";
import { Column } from "components/GenericListScreen/types";
import { useQueryClient } from "@tanstack/react-query";

import AddCampaignDialog from "./components/AddCampaignDialog/AddCampaignDialog";
import CampaignHistoryDialog from "./components/CampaignHistoryDialog/CampaignHistoryDialog";

// Status Colors Helper
const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
        case "DRAFT": return "secondary";
        case "SCHEDULED": return "info";
        case "SENDING": return "warning";
        case "COMPLETED": return "success";
        case "FAILED": return "error";
        case "CANCELLED": return "default";
        default: return "secondary";
    }
};

const getStatusLabel = (status: string) => {
    switch (status) {
        case "DRAFT": return "임시저장";
        case "SCHEDULED": return "예약됨";
        case "SENDING": return "발송중";
        case "COMPLETED": return "완료";
        case "FAILED": return "실패";
        case "CANCELLED": return "취소됨";
        default: return status;
    }
};

export default function PushCampaignListScreen() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const queryClient = useQueryClient();

    // State
    const [statusFilter, setStatusFilter] = useState("");
    const [addCampaignDialogOpen, setAddCampaignDialogOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState<any | null>(null);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [historyViewingCampaign, setHistoryViewingCampaign] = useState<any | null>(null);

    // Handlers
    const handleOpenAddDialog = () => {
        setEditingCampaign(null);
        setAddCampaignDialogOpen(true);
    };

    const handleOpenEditDialog = (campaign: any) => {
        setEditingCampaign(campaign);
        setAddCampaignDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setAddCampaignDialogOpen(false);
        setEditingCampaign(null);
    };

    const handleDialogSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ["PushCampaign.pagedList"] });
    };

    const handleOpenHistoryDialog = (campaign: any) => {
        setHistoryViewingCampaign(campaign);
        setHistoryDialogOpen(true);
    };

    const handleCloseHistoryDialog = () => {
        setHistoryDialogOpen(false);
        setHistoryViewingCampaign(null);
    };

    const handleDelete = async (campaign: any) => {
        const result = await fireAlert({
            title: "캠페인 삭제",
            text: `"${campaign.name}" 캠페인을 삭제하시겠습니까?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "삭제",
            cancelButtonText: "취소",
        }, darkMode);

        if (result.isConfirmed) {
            try {
                await request("PushCampaign.delete", {
                    pathParams: { id: campaign.seqId },
                });

                fireAlert({
                    icon: "success",
                    title: "삭제 완료",
                    text: "캠페인이 성공적으로 삭제되었습니다.",
                    timer: 1500,
                    showConfirmButton: false,
                }, darkMode);

                queryClient.invalidateQueries({ queryKey: ["PushCampaign.pagedList"] });
            } catch (error: any) {
                console.error("캠페인 삭제 실패:", error);
                fireAlert({
                    icon: "error",
                    title: "삭제 실패",
                    text: error?.response?.data?.message || "캠페인 삭제에 실패했습니다.",
                }, darkMode);
            }
        }
    };

    const handleSendNow = async (campaign: any) => {
        const result = await fireAlert({
            title: "즉시 발송",
            text: `"${campaign.name}" 캠페인을 지금 발송하시겠습니까?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "발송",
            cancelButtonText: "취소",
        }, darkMode);

        if (result.isConfirmed) {
            try {
                const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
                const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
                const tenantSeqId = savedTenantId ? parseInt(savedTenantId, 10) : undefined;

                if (!tenantSeqId) {
                    fireAlert({
                        icon: "error",
                        title: "오류",
                        text: "테넌트 정보를 찾을 수 없습니다.",
                    }, darkMode);
                    return;
                }

                await request("PushCampaign.sendNow", {
                    pathParams: { id: campaign.seqId },
                    paramz: { TenantSeqId: tenantSeqId },
                });

                fireAlert({
                    icon: "success",
                    title: "발송 시작",
                    text: "캠페인 발송이 시작되었습니다.",
                    timer: 1500,
                    showConfirmButton: false,
                }, darkMode);

                queryClient.invalidateQueries({ queryKey: ["PushCampaign.pagedList"] });
            } catch (error: any) {
                console.error("캠페인 발송 실패:", error);
                fireAlert({
                    icon: "error",
                    title: "발송 실패",
                    text: error?.response?.data?.message || "캠페인 발송에 실패했습니다.",
                }, darkMode);
            }
        }
    };

    // Columns Definition
    const columns: Column<any>[] = useMemo(() => [
        {
            Header: "캠페인 정보",
            accessor: "name",
            width: "30%",
            Cell: ({ row }: { row: { original: any } }) => (
                <MDBox lineHeight={1.5}>
                    <MDTypography display="block" variant="button" fontWeight="medium">
                        {row.original.name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                        {row.original.title}
                    </MDTypography>
                    {row.original.topic && (
                        <MDTypography variant="caption" color="text" display="block">
                            Topic: {row.original.topic}
                        </MDTypography>
                    )}
                </MDBox>
            ),
        },
        {
            Header: "상태",
            accessor: "status",
            width: "10%",
            align: "center",
            Cell: ({ value }: { value: string }) => (
                <Chip
                    label={getStatusLabel(value)}
                    color={getStatusColor(value)}
                    size="small"
                />
            ),
        },
        {
            Header: "일정 / 발송일",
            accessor: "scheduledDt",
            width: "20%",
            Cell: ({ row }: { row: { original: any } }) => (
                <MDBox>
                    {row.original.scheduledDt && (
                        <MDTypography variant="caption" color="info" display="block">
                            예약: {formatLocalDateTime(row.original.scheduledDt)}
                        </MDTypography>
                    )}
                    {row.original.startedDt && (
                        <MDTypography variant="caption" color="text" display="block">
                            시작: {formatLocalDateTime(row.original.startedDt)}
                        </MDTypography>
                    )}
                    {row.original.completedDt && (
                        <MDTypography variant="caption" color="success" display="block">
                            완료: {formatLocalDateTime(row.original.completedDt)}
                        </MDTypography>
                    )}
                    {!row.original.scheduledDt && !row.original.startedDt && (
                        <MDTypography variant="caption" color="text">
                            -
                        </MDTypography>
                    )}
                </MDBox>
            ),
        },
        {
            Header: "발송 통계",
            accessor: "stats",
            width: "25%",
            Cell: ({ row }: { row: { original: any } }) => (
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" display="block">
                            발송: {row.original.sentCount || 0}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" color="text" display="block">
                            실패: {row.original.failedCount || 0}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" color="success" display="block">
                            전달: {row.original.deliveredCount || 0}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" color="info" display="block">
                            읽음: {row.original.readCount || 0}
                        </MDTypography>
                    </Grid>
                </Grid>
            ),
        },
        {
            Header: "관리",
            accessor: "actions",
            width: "15%",
            align: "right",
            Cell: ({ row }: { row: { original: any } }) => (
                <MDBox display="flex" gap={1} justifyContent="flex-end">
                    <Tooltip title="발송 이력">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleOpenHistoryDialog(row.original); }}
                            sx={{
                                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                "&:hover": { color: "#9c27b0" },
                            }}
                        >
                            <Icon fontSize="small">history</Icon>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="수정">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(row.original); }}
                            sx={{
                                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                "&:hover": { color: "#49a3f1" },
                            }}
                        >
                            <Icon fontSize="small">edit</Icon>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="이력">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleOpenHistoryDialog(row.original); }}
                            sx={{
                                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                "&:hover": { color: "#1A73E8" },
                            }}
                        >
                            <Icon fontSize="small">history</Icon>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="삭제">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleDelete(row.original); }}
                            sx={{
                                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                "&:hover": { color: "#f44336" },
                            }}
                        >
                            <Icon fontSize="small">delete</Icon>
                        </IconButton>
                    </Tooltip>

                    {(row.original.status === "DRAFT" || row.original.status === "PENDING") && (
                        <Tooltip title="즉시 발송">
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleSendNow(row.original); }}
                                sx={{
                                    color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                    "&:hover": { color: "#4caf50" },
                                }}
                            >
                                <Icon fontSize="small">send</Icon>
                            </IconButton>
                        </Tooltip>
                    )}
                </MDBox>
            ),
        },
    ], [darkMode, handleOpenEditDialog, handleDelete, handleSendNow, handleOpenHistoryDialog]);

    const extraParams = useMemo(() => {
        const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
        const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
        return {
            Status: statusFilter || undefined,
            TenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
        };
    }, [statusFilter]);


    const actionComponent = (
        <MDBox display="flex" gap={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="status-filter-label" sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>상태 필터</InputLabel>
                <Select
                    labelId="status-filter-label"
                    value={statusFilter}
                    label="상태 필터"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ height: 40 }}
                >
                    <MenuItem value="">전체 상태</MenuItem>
                    <MenuItem value="DRAFT">임시저장</MenuItem>
                    <MenuItem value="SCHEDULED">예약됨</MenuItem>
                    <MenuItem value="SENDING">발송중</MenuItem>
                    <MenuItem value="COMPLETED">완료</MenuItem>
                    <MenuItem value="FAILED">실패</MenuItem>
                    <MenuItem value="CANCELLED">취소됨</MenuItem>
                </Select>
            </FormControl>
            <MDButton variant="gradient" color="info" size="small" onClick={handleOpenAddDialog}>
                <Icon>add</Icon>&nbsp; 캠페인 생성
            </MDButton>
        </MDBox>
    );

    return (
        <>
            <GenericListScreen
                title="푸시 캠페인 목록 ( PUSH CAMPAIGNS )"
                icon="campaign"
                apiName="PushCampaign.pagedList"
                columns={columns}
                actionComponent={actionComponent}
                extraParams={extraParams}
            />
            <AddCampaignDialog
                open={addCampaignDialogOpen}
                editingCampaign={editingCampaign}
                onClose={handleCloseDialog}
                onSuccess={handleDialogSuccess}
                darkMode={darkMode}
            />
            <CampaignHistoryDialog
                open={historyDialogOpen}
                campaign={historyViewingCampaign}
                onClose={handleCloseHistoryDialog}
            />
        </>
    );
}
